// Phase 15 · Portal Screen — My Stock. Same layout/behavior as SE/ASM's
// "Distributor Stock" screen (frontend/src/pages/field/DistributorStockSE.jsx)
// — MRP, Total KG summary, bordered cards, edit-in-KG with a pencil icon —
// rather than a bespoke, plainer layout. Editing is gated behind the
// EDIT_DISTRIBUTOR_STOCK workflow permission (same per-user toggle SE/ASM
// already have for a MAPPED distributor's stock). Bulk upload removed —
// a distributor manages one distributor's stock, not a sheet of many;
// that tool is for SE/admin correcting many distributors at once.
import { useEffect, useMemo, useState } from "react";
import { Search, Boxes, Loader2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function PortalStock() {
  const { hasWorkflowPerm } = useAuth();
  const canEdit = hasWorkflowPerm("EDIT_DISTRIBUTOR_STOCK");
  const [items, setItems] = useState([]);
  const [totalKg, setTotalKg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/distributor-stock", { params: { page_size: 500 } })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotalKg(data.total_kg || 0);
        setDisabled(false);
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (detail?.error === "stock_visibility_disabled") setDisabled(true);
        else toast.error(formatApiError(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((it) =>
      (it.sku_code || "").toLowerCase().includes(query)
      || (it.sku_name || "").toLowerCase().includes(query));
  }, [items, q]);

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditVal(String(Number(it.on_hand_kg || 0)));
  };

  const saveEdit = async (it) => {
    const val = parseFloat(editVal);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid quantity"); return; }
    setSaving(true);
    try {
      await api.patch(`/distributor-stock/${it.id}`, { on_hand_kg: val });
      toast.success("Stock updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!loading && disabled) {
    return (
      <div className="p-4 pb-24" data-testid="portal-stock-page">
        <h1 className="text-xl font-extrabold text-neutral-900 mb-3">My Stock</h1>
        <div className="text-center py-12 text-sm text-neutral-500 bg-neutral-50 rounded-lg border"
             data-testid="stock-visibility-disabled">
          Stock visibility has been disabled for your account by the admin.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="portal-stock-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1">
          <Boxes className="h-3.5 w-3.5" /> Distributor Stock
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">Current availability</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search SKU code / name…"
               className="pl-9 h-10" data-testid="stock-search" />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 flex items-center justify-between"
           data-testid="portal-stock-total-kg">
        <span className="text-xs uppercase tracking-widest text-neutral-500">Total KG</span>
        <span className="text-lg font-extrabold text-neutral-900">{Number(totalKg).toFixed(2)} kg</span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="py-10 text-center text-neutral-400 text-sm" data-testid="stock-empty">
          No stock rows found.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((it) => {
          const isEditing = editingId === it.id;
          return (
            <Card key={it.id} className="border-neutral-200 rounded-xl" data-testid={`stock-row-${it.sku_code}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] text-neutral-400 font-mono">{it.sku_code}</div>
                  <div className="text-sm font-semibold text-neutral-900 truncate">{it.sku_name || "—"}</div>
                  {it.mrp != null && (
                    <div className="text-[11px] text-neutral-500">MRP ₹{it.mrp}</div>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Input type="number" min="0" step="0.01" value={editVal}
                           onChange={(e) => setEditVal(e.target.value)}
                           className="w-24 h-9 text-right"
                           data-testid={`stock-qty-${it.sku_code}`} />
                    <span className="text-xs text-neutral-400">kg</span>
                    <Button size="icon" variant="ghost" disabled={saving} onClick={() => saveEdit(it)}
                            data-testid={`stock-save-${it.sku_code}`}>
                      <Check className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button size="icon" variant="ghost" disabled={saving} onClick={() => setEditingId(null)}>
                      <X className="h-4 w-4 text-neutral-400" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <div className="text-lg font-bold text-neutral-900" data-testid={`stock-qty-${it.sku_code}`}>
                        {Number(it.on_hand_kg || 0).toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400">kg on hand</div>
                    </div>
                    {canEdit && (
                      <Button size="icon" variant="ghost" onClick={() => startEdit(it)}
                              data-testid={`stock-edit-${it.sku_code}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
